import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { SwapApiCounterparty, SwapApiItem, SwapApiReceiveLeg, SwapRow } from './swaps.types';
import { apiUrl } from '../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapsService {
  private readonly endpoint = apiUrl('Swap');

  constructor(private readonly http: HttpClient) {}

  getSwaps(): Observable<SwapRow[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.toItems(response).map((item) => this.normalizeItem(item))),
    );
  }

  private normalizeItem(item: SwapApiItem): SwapRow {
    const counterparty = this.toCounterparty(item.counterParty);
    const receiveLeg = this.toReceiveLeg(item.receiveLeg);

    return {
      dealId: this.toText(item.dealId),
      tradeId: this.toText(item.tradeId),
      rfrTradeId: this.toText(item.rfrTradeId),
      structureId: this.toText(item.structureId),
      counterparty: this.normalizeCounterparty(counterparty),
      folder: this.toText(item.folder),
      tradeDate: this.toText(item.tradeDate),
      settlementDate: this.toText(item.settlementDate),
      maturityDate: this.toText(item.maturityDate),
      payLegCurrency: this.toText(item.currency),
      payLegNotional: this.toNumber(item.notional),
      receiveLegCurrency: this.toText(receiveLeg.ccy),
      receiveLegNotional: this.toNumber(receiveLeg.notional),
    };
  }

  private toItems(value: unknown): SwapApiItem[] {
    return Array.isArray(value) ? (value as SwapApiItem[]) : [];
  }

  private toCounterparty(value: unknown): SwapApiCounterparty {
    return this.isRecord(value) ? (value as SwapApiCounterparty) : {};
  }

  private toReceiveLeg(value: unknown): SwapApiReceiveLeg {
    return this.isRecord(value) ? (value as SwapApiReceiveLeg) : {};
  }

  private normalizeCounterparty(counterparty: SwapApiCounterparty): string {
    const legalName = this.toText(counterparty.legalName);
    if (legalName) {
      return legalName;
    }

    return this.toText(counterparty.id);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    }

    return Number.NaN;
  }
}
